import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import DescriptionWithTags from '../../components/DescriptionWithTags/DescriptionWithTags';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import envConfig from '../../utils/EnvironmentConfigs.js';

const resolveGroupId = (group) => group?.id ?? group?.uuid ?? group?.name;
const resolveRoleId = (role) => role?.id ?? role?.uuid ?? role?.name;
const RESCIND_BLOCKED_ROLE_TAG = 'unlock';
const isRoleRescindBlocked = (role) => String(role?.clientName ?? '').trim() === RESCIND_BLOCKED_ROLE_TAG;

const UserPage = ({ authDetails, onSignOut }) => {
    const [userResponse, setUserResponse] = useState(null);
    const [rolesResponse, setRolesResponse] = useState(null);
    const [userError, setUserError] = useState('');
    const [rolesError, setRolesError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingRoles, setIsLoadingRoles] = useState(true);
    const [isRescindingGroups, setIsRescindingGroups] = useState(false);
    const [groupRescindSuccess, setGroupRescindSuccess] = useState('');
    const [groupRescindError, setGroupRescindError] = useState('');
    const [isRescindingRoles, setIsRescindingRoles] = useState(false);
    const [roleRescindSuccess, setRoleRescindSuccess] = useState('');
    const [roleRescindError, setRoleRescindError] = useState('');
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
    const [selectedRoleIds, setSelectedRoleIds] = useState(new Set());
    const subject = authDetails?.identity?.tokenParsed?.sub ?? authDetails?.identity?.subject ?? null;

    const groups = useMemo(
        () => (Array.isArray(userResponse) ? userResponse : userResponse?.groups ?? []),
        [userResponse]
    );

    useEffect(() => {
        let isMounted = true;
        const apiUrl = envConfig.apiBaseUrl;

        if (!apiUrl) {
            setUserError('API URL is not configured.');
            setIsLoading(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchUserData = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}/groups`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setUserResponse(response.data.content);
                }
            } catch (error) {
                if (isMounted) {
                    setUserError('We could not load your access details.');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        const fetchRolesData = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject ?? '')}/roles`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setRolesResponse(response.data);
                }
            } catch (error) {
                if (isMounted) {
                    setRolesError('We could not load your role details.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRoles(false);
                }
            }
        };

        fetchUserData();
        fetchRolesData();

        return () => {
            isMounted = false;
        };
    }, [subject]);

    const allGroupIds = useMemo(
        () => groups.map(resolveGroupId).filter(Boolean),
        [groups]
    );

    const roles = useMemo(() => {
        if (!rolesResponse) {
            return [];
        }

        const realmRoles = Array.isArray(rolesResponse.realmMappings)
            ? rolesResponse.realmMappings.map((role) => ({
                ...role,
                clientName: null,
            }))
            : [];
        const clientMappings = rolesResponse.clientMappings ?? {};
        const clientRoles = Object.values(clientMappings).flatMap((client) =>
            Array.isArray(client?.mappings)
                ? client.mappings.map((role) => ({
                    ...role,
                    clientName: client.client ?? client.clientId ?? null,
                }))
                : []
        );

        return [...realmRoles, ...clientRoles];
    }, [rolesResponse]);

    const allRoleIds = useMemo(
        () => roles.filter((role) => !isRoleRescindBlocked(role)).map(resolveRoleId).filter(Boolean),
        [roles]
    );

    const toggleGroupSelection = (groupId) => {
        setGroupRescindSuccess('');
        setGroupRescindError('');
        setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const toggleRoleSelection = (role) => {
        const roleId = resolveRoleId(role);
        if (!roleId || isRoleRescindBlocked(role)) {
            return;
        }

        setRoleRescindSuccess('');
        setRoleRescindError('');
        setSelectedRoleIds((prev) => {
            const next = new Set(prev);
            if (next.has(roleId)) {
                next.delete(roleId);
            } else {
                next.add(roleId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        setGroupRescindSuccess('');
        setGroupRescindError('');
        setSelectedGroupIds(new Set(allGroupIds));
    };

    const handleUnselectAll = () => {
        setGroupRescindSuccess('');
        setGroupRescindError('');
        setSelectedGroupIds(new Set());
    };

    const handleSelectAllRoles = () => {
        setRoleRescindSuccess('');
        setRoleRescindError('');
        setSelectedRoleIds(new Set(allRoleIds));
    };

    const handleUnselectAllRoles = () => {
        setRoleRescindSuccess('');
        setRoleRescindError('');
        setSelectedRoleIds(new Set());
    };

    const handleRescindAccess = async () => {
        if (selectedGroupIds.size === 0) {
            return;
        }

        if (!subject) {
            setGroupRescindError('We could not verify your user identity.');
            return;
        }

        const selectedGroups = groups
            .map((group) => ({
                id: resolveGroupId(group),
                name: group?.name ?? resolveGroupId(group),
            }))
            .filter((group) => group.id && selectedGroupIds.has(group.id));

        if (selectedGroups.length === 0) {
            return;
        }

        setIsRescindingGroups(true);
        setGroupRescindSuccess('');
        setGroupRescindError('');

        try {
            const results = await Promise.allSettled(
                selectedGroups.map((group) =>
                    ApiClient.post(
                        `/user/${encodeURIComponent(subject)}/group/${encodeURIComponent(group.id)}/rescind`
                    )
                )
            );

            const successfulGroupIds = new Set();
            const failedGroupNames = [];

            results.forEach((result, index) => {
                const selectedGroup = selectedGroups[index];
                const status = result.status === 'fulfilled' ? result.value?.status : null;
                const didSucceed = status && status >= 200 && status < 300;

                if (didSucceed) {
                    successfulGroupIds.add(selectedGroup.id);
                } else {
                    failedGroupNames.push(selectedGroup.name);
                }
            });

            if (successfulGroupIds.size > 0) {
                setUserResponse((previous) => {
                    if (Array.isArray(previous)) {
                        return previous.filter((group) => !successfulGroupIds.has(resolveGroupId(group)));
                    }

                    if (Array.isArray(previous?.groups)) {
                        return {
                            ...previous,
                            groups: previous.groups.filter(
                                (group) => !successfulGroupIds.has(resolveGroupId(group))
                            ),
                        };
                    }

                    return previous;
                });

                setSelectedGroupIds((previous) => {
                    const next = new Set(previous);
                    successfulGroupIds.forEach((groupId) => next.delete(groupId));
                    return next;
                });
            }

            if (failedGroupNames.length === 0) {
                const succeededNames = selectedGroups.map((group) => group.name).join(', ');
                setGroupRescindSuccess(`Rescinded access for: ${succeededNames}`);
                return;
            }

            if (successfulGroupIds.size > 0) {
                setGroupRescindError(`Some groups could not be rescinded: ${failedGroupNames.join(', ')}`);
                return;
            }

            setGroupRescindError(`We could not rescind access for: ${failedGroupNames.join(', ')}`);
        } finally {
            setIsRescindingGroups(false);
        }
    };

    const handleRescindRoles = async () => {
        if (selectedRoleIds.size === 0) {
            return;
        }

        if (!subject) {
            setRoleRescindError('We could not verify your user identity.');
            return;
        }

        const selectedRoles = roles
            .map((role) => ({
                id: resolveRoleId(role),
                name: role?.name ?? resolveRoleId(role),
                blocked: isRoleRescindBlocked(role),
            }))
            .filter((role) => role.id && selectedRoleIds.has(role.id));

        if (selectedRoles.length === 0) {
            return;
        }

        const blockedRoleNames = selectedRoles.filter((role) => role.blocked).map((role) => role.name);
        const rescindableRoles = selectedRoles.filter((role) => !role.blocked);

        if (rescindableRoles.length === 0) {
            setRoleRescindError(`Roles tagged ${RESCIND_BLOCKED_ROLE_TAG} cannot be rescinded.`);
            return;
        }

        setIsRescindingRoles(true);
        setRoleRescindSuccess('');
        setRoleRescindError('');

        try {
            const results = await Promise.allSettled(
                rescindableRoles.map((role) =>
                    ApiClient.post(
                        `/user/${encodeURIComponent(subject)}/role/${encodeURIComponent(role.id)}/rescind`
                    )
                )
            );

            const successfulRoleIds = new Set();
            const failedRoleNames = [];

            results.forEach((result, index) => {
                const selectedRole = rescindableRoles[index];
                const status = result.status === 'fulfilled' ? result.value?.status : null;
                const didSucceed = status && status >= 200 && status < 300;

                if (didSucceed) {
                    successfulRoleIds.add(selectedRole.id);
                } else {
                    failedRoleNames.push(selectedRole.name);
                }
            });

            if (successfulRoleIds.size > 0) {
                setRolesResponse((previous) => {
                    if (!previous) {
                        return previous;
                    }

                    const nextRealmMappings = Array.isArray(previous.realmMappings)
                        ? previous.realmMappings.filter((role) => !successfulRoleIds.has(resolveRoleId(role)))
                        : previous.realmMappings;
                    const nextClientMappings = Object.entries(previous.clientMappings ?? {}).reduce(
                        (accumulator, [clientKey, client]) => {
                            if (Array.isArray(client?.mappings)) {
                                accumulator[clientKey] = {
                                    ...client,
                                    mappings: client.mappings.filter(
                                        (clientRole) => !successfulRoleIds.has(resolveRoleId(clientRole))
                                    ),
                                };
                            } else {
                                accumulator[clientKey] = client;
                            }
                            return accumulator;
                        },
                        {}
                    );

                    return {
                        ...previous,
                        realmMappings: nextRealmMappings,
                        clientMappings: nextClientMappings,
                    };
                });

                setSelectedRoleIds((previous) => {
                    const next = new Set(previous);
                    successfulRoleIds.forEach((roleId) => next.delete(roleId));
                    return next;
                });
            }

            const successfulRoleNames = rescindableRoles
                .filter((role) => successfulRoleIds.has(role.id))
                .map((role) => role.name);
            if (successfulRoleNames.length > 0) {
                setRoleRescindSuccess(`Rescinded roles: ${successfulRoleNames.join(', ')}`);
            }

            const errorMessages = [];
            if (failedRoleNames.length > 0) {
                errorMessages.push(`Could not rescind roles: ${failedRoleNames.join(', ')}`);
            }
            if (blockedRoleNames.length > 0) {
                errorMessages.push(
                    `Roles tagged ${RESCIND_BLOCKED_ROLE_TAG} cannot be rescinded: ${blockedRoleNames.join(', ')}`
                );
            }

            if (errorMessages.length > 0) {
                setRoleRescindError(errorMessages.join(' '));
            }
        } finally {
            setIsRescindingRoles(false);
        }
    };

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Current Group entitlements granted</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {groupRescindSuccess ? (
                                            <div className="usa-alert usa-alert--success margin-bottom-2" role="status">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{groupRescindSuccess}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        {groupRescindError ? (
                                            <div className="usa-alert usa-alert--error margin-bottom-2" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{groupRescindError}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        {isLoading ? (
                                            <div className="usa-form" aria-hidden="true">
                                                <div className="usa-button-group">
                                                    <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    <ValueWithSkeleton loading type="chip" width="9ch" />
                                                    <ValueWithSkeleton loading type="chip" width="10ch" />
                                                </div>
                                                <fieldset className="usa-fieldset">
                                                    {Array.from({ length: 3 }).map((_, index) => (
                                                        <div key={`user-group-loading-${index}`} className="usa-checkbox">
                                                            <input
                                                                className="usa-checkbox__input usa-checkbox__input--tile"
                                                                type="checkbox"
                                                                disabled
                                                            />
                                                            <label className="usa-checkbox__label">
                                                                <ValueWithSkeleton loading type="sentence" width="16ch" />
                                                                <span className="usa-checkbox__label-description">
                                                                    <ValueWithSkeleton loading type="sentence" width="26ch" />
                                                                </span>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </fieldset>
                                            </div>
                                        ) : userError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{userError}</p>
                                                </div>
                                            </div>
                                        ) : groups.length === 0 ? (
                                            <p>No access groups are currently assigned to your account.</p>
                                        ) : (
                                            <div className="usa-form">
                                                <div className="usa-button-group">
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--outline"
                                                        onClick={handleSelectAll}
                                                        disabled={isRescindingGroups}
                                                    >
                                                        Select all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--outline"
                                                        onClick={handleUnselectAll}
                                                        disabled={isRescindingGroups}
                                                    >
                                                        Unselect all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--secondary"
                                                        onClick={handleRescindAccess}
                                                        disabled={selectedGroupIds.size === 0 || isRescindingGroups}
                                                    >
                                                        {isRescindingGroups ? 'Rescinding...' : 'Rescind access'}
                                                    </button>
                                                </div>
                                                <fieldset className="usa-fieldset">
                                                    {groups.map((group) => {
                                                        const groupId = resolveGroupId(group);
                                                        return (
                                                            <div key={groupId} className="usa-checkbox">
                                                                <input
                                                                    className="usa-checkbox__input usa-checkbox__input--tile"
                                                                    id={`group-${groupId}`}
                                                                    type="checkbox"
                                                                    name="access-groups"
                                                                    checked={selectedGroupIds.has(groupId)}
                                                                    onChange={() => toggleGroupSelection(groupId)}
                                                                    disabled={isRescindingGroups}
                                                                />
                                                                <label className="usa-checkbox__label" htmlFor={`group-${groupId}`}>
                                                                    {group.name}
                                                                    <span className="usa-checkbox__label-description">
                                                                        <DescriptionWithTags text={group.description} />
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </fieldset>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Current roles assigned</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {roleRescindSuccess ? (
                                            <div className="usa-alert usa-alert--success margin-bottom-2" role="status">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{roleRescindSuccess}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        {roleRescindError ? (
                                            <div className="usa-alert usa-alert--error margin-bottom-2" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{roleRescindError}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        {isLoadingRoles ? (
                                            <div className="usa-form" aria-hidden="true">
                                                <div className="usa-button-group">
                                                    <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    <ValueWithSkeleton loading type="chip" width="9ch" />
                                                    <ValueWithSkeleton loading type="chip" width="10ch" />
                                                </div>
                                                <fieldset className="usa-fieldset">
                                                    {Array.from({ length: 3 }).map((_, index) => (
                                                        <div key={`user-role-loading-${index}`} className="usa-checkbox">
                                                            <input
                                                                className="usa-checkbox__input usa-checkbox__input--tile"
                                                                type="checkbox"
                                                                disabled
                                                            />
                                                            <label className="usa-checkbox__label">
                                                                <ValueWithSkeleton loading type="chip" width="6ch" />{' '}
                                                                <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                                <span className="usa-checkbox__label-description">
                                                                    <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                </span>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </fieldset>
                                            </div>
                                        ) : rolesError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{rolesError}</p>
                                                </div>
                                            </div>
                                        ) : roles.length === 0 ? (
                                            <p>No roles are currently assigned to your account.</p>
                                        ) : (
                                            <div className="usa-form">
                                                <div className="usa-button-group">
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--outline"
                                                        onClick={handleSelectAllRoles}
                                                        disabled={isRescindingRoles}
                                                    >
                                                        Select all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--outline"
                                                        onClick={handleUnselectAllRoles}
                                                        disabled={isRescindingRoles}
                                                    >
                                                        Unselect all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--secondary"
                                                        onClick={handleRescindRoles}
                                                        disabled={selectedRoleIds.size === 0 || isRescindingRoles}
                                                    >
                                                        {isRescindingRoles ? 'Rescinding...' : 'Rescind access'}
                                                    </button>
                                                </div>
                                                <fieldset className="usa-fieldset">
                                                    {roles.map((role) => {
                                                        const roleId = resolveRoleId(role);
                                                        const isRescindBlocked = isRoleRescindBlocked(role);
                                                        return (
                                                            <div key={roleId} className="usa-checkbox">
                                                                <input
                                                                    className="usa-checkbox__input usa-checkbox__input--tile"
                                                                    id={`role-${roleId}`}
                                                                    type="checkbox"
                                                                    name="access-roles"
                                                                    checked={selectedRoleIds.has(roleId)}
                                                                    onChange={() => toggleRoleSelection(role)}
                                                                    disabled={isRescindingRoles || isRescindBlocked}
                                                                />
                                                                <label className="usa-checkbox__label" htmlFor={`role-${roleId}`}>
                                                                    {role.clientName ? (
                                                                        <span className="usa-tag usa-tag--cool">{role.clientName}</span>
                                                                    ) : null}
                                                                    {role.name}
                                                                    <span className="usa-checkbox__label-description">
                                                                        {role.description || 'No description provided.'}
                                                                    </span>
                                                                    {isRescindBlocked ? (
                                                                        <span className="usa-checkbox__label-description">
                                                                            Roles tagged {RESCIND_BLOCKED_ROLE_TAG} cannot be rescinded.
                                                                        </span>
                                                                    ) : null}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </fieldset>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default UserPage;
